import Link from "next/link";
import { useRouter } from "next/router";
import { signIn } from "next-auth/react";
import { useCallback, useState } from "react";
import { toast } from "sonner";
import { LoginOptions } from "@/components/auth/login-options";
import {
  getNextAuthSignInErrorMessage,
  OAUTH_ACCOUNT_NOT_LINKED_ERROR,
} from "@/lib/auth/auth-error-codes";
import {
  getClientPostAuthDestination,
  getFirstQueryParam,
} from "@/lib/auth/callback-url";
import type { PreferredSignInProvider } from "@/lib/auth/preferred-sign-in-provider";
import { setPreferredSignInProvider } from "@/lib/auth/preferred-sign-in-provider";
import { resolveLoginProviders } from "@/lib/auth/resolve-login-providers";
import type { UserLoginValues } from "@/lib/schemas/login";

function getAuthErrorMessage(error?: string) {
  return getNextAuthSignInErrorMessage(error);
}

export function UserLoginForm({
  preferredProvider = null,
  qaLoginEnabled = false,
}: {
  preferredProvider?: PreferredSignInProvider | null;
  qaLoginEnabled?: boolean;
}) {
  const router = useRouter();
  const error = getFirstQueryParam(router.query.error);
  const isOAuthAccountNotLinked = error === OAUTH_ACCOUNT_NOT_LINKED_ERROR;
  const authErrorMessage = getAuthErrorMessage(error);

  const [loading, setLoading] = useState({
    credentials: false,
    google: false,
  });

  const { primary, accordion } = resolveLoginProviders({
    preferred: preferredProvider,
    qaLoginEnabled,
  });

  const onSubmit = useCallback(async (data: UserLoginValues) => {
    const postAuthDestination = getClientPostAuthDestination();

    setLoading((value) => ({
      ...value,
      credentials: true,
    }));

    try {
      setPreferredSignInProvider("email");
      const res = await signIn("email", {
        redirectTo: postAuthDestination,
        email: data.email.toLowerCase(),
        redirect: false,
      });
      if (res?.error || res?.url?.includes("error")) {
        toast.error("Uh oh! Something went wrong.", {
          description: res?.error,
        });
        return;
      }

      toast.success("Check your email", {
        description:
          "We sent you a login link. Be sure to check your spam too.",
      });
    } catch (_e) {
      toast.error("Uh oh! Something went wrong.", {
        description: "Failed to with email. Try again",
      });
    } finally {
      setLoading((value) => ({
        ...value,
        credentials: false,
      }));
    }
  }, []);

  const onGoogleLogin = useCallback(async () => {
    const postAuthDestination = getClientPostAuthDestination();

    setLoading((value) => ({
      ...value,
      google: true,
    }));

    try {
      setPreferredSignInProvider("google");
      await signIn("google", { redirectTo: postAuthDestination });
    } catch (_e) {
      toast.error("Uh oh! Something went wrong.", {
        description: "Failed to with google. Try again",
      });
      setLoading((value) => ({
        ...value,
        google: false,
      }));
    }
  }, []);

  const isLoading = loading.credentials || loading.google;

  return (
    <div className="flex flex-1 flex-col space-y-4">
      {authErrorMessage ? (
        isOAuthAccountNotLinked ? (
          <p className="rounded-md border border-score-amber/30 bg-score-amber-soft p-3 text-score-amber text-xs">
            {authErrorMessage}
          </p>
        ) : (
          <p className="rounded-md border border-destructive/30 bg-score-red-soft p-3 text-destructive text-xs">
            {authErrorMessage}
          </p>
        )
      ) : null}

      <LoginOptions
        accordion={accordion}
        primary={primary}
        providerProps={{
          credentialsLoading: loading.credentials,
          disabled: isLoading,
          googleLoading: loading.google,
          onEmailSubmit: onSubmit,
          onGoogleLogin,
        }}
      />

      <p className="text-muted-foreground text-xs">
        By clicking login, you acknowledge that you have read and agree to
        VacWork's{" "}
        <Link
          className="underline"
          href="/terms"
          rel="noreferrer"
          target="_blank"
        >
          Terms of Service
        </Link>{" "}
        and{" "}
        <Link
          className="underline"
          href="/privacy"
          rel="noreferrer"
          target="_blank"
        >
          Privacy Policy
        </Link>
        .
      </p>
    </div>
  );
}
